import {useEffect, useMemo, useState} from "react";
import PrimaryButton from "@/Components/PrimaryButton";
import {faGear, faMagicWandSparkles} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ucFirst} from "@/utils/textFormatting";
import {MediaMetaData, SubtitlePosition} from "@/types";
import MediaProbe from "@/services/media/MediaProbe";
import {MultiViewModal} from "@/Components/MultiViewModal";
import SubtitleRegionConfig from "@/Pages/Dashboard/Partials/SubtitleRegionSelect";
import {ToggleSwitch} from "flowbite-react";
import Spinner from "@/Components/Spinner";
import TimeRangePicker from "@/Components/TimeRangePicker";
import toast from "react-hot-toast";

export type AdvancedSettings = {
    position: SubtitlePosition,
    yOffsetRatio?: [number, number],
    xOffsetRatio?: [number, number],
    softCodedSubtitles: boolean,
    filterNoiseText: boolean,
    timeStart?: number,
    timeEnd?: number,
}

export type AdvancedSettingsModalProps = {
    show: boolean,
    onClose: () => void,
    onSave: (settings: AdvancedSettings) => void,
    initSettings: AdvancedSettings,
    videoFile?: File,
    metadata?: MediaMetaData,
    isAudioOnly?: boolean,
}

export const DEFAULT_SUBTITLE_POSITION = 'auto';

export const DEFAULT_SETTINGS: AdvancedSettings = {
    position: DEFAULT_SUBTITLE_POSITION,
    softCodedSubtitles: true,
    filterNoiseText: true,
    timeStart: 0,
    timeEnd: undefined,
}

export default function AdvancedSettingsModal({
  show,
  onClose,
  onSave,
  initSettings,
  videoFile,
  metadata,
  isAudioOnly
}: AdvancedSettingsModalProps) {
    const [position, setPosition] = useState<SubtitlePosition>(initSettings.position);
    const [mediaMetadata, setMediaMetadata] = useState<null | MediaMetaData>(null);
    const [selectingCustomRegion, setSelectingCustomRegion] = useState(false);
    const [yOffset, setYOffset] = useState<undefined | [number, number]>(initSettings.yOffsetRatio);
    const [xOffset, setXOffset] = useState<undefined | [number, number]>(initSettings.xOffsetRatio);
    const [softCodedSubtitles, setSoftCodedSubtitles] = useState(initSettings.softCodedSubtitles);
    const [filterNoiseText, setFilterNoiseText] = useState<boolean>(true);
    const [timeStart, setTimeStart] = useState<number>(initSettings.timeStart || 0);
    const [timeEnd, setTimeEnd] = useState<number | undefined>(initSettings.timeEnd);
    const mediaDuration = useMemo(
        () => mediaMetadata ? Math.ceil([...mediaMetadata.videos, ...mediaMetadata.audio][0]?.duration || 0): 0,
        [mediaMetadata]
    )
    const canSelectCustomRegion = useMemo(() => {
        return (mediaMetadata?.videos.length !== 0) &&
            !isAudioOnly &&
            (mediaMetadata?.subtitles.length === 0 || !softCodedSubtitles)
    }, [mediaMetadata, softCodedSubtitles, isAudioOnly]);
    const hasSoftCodedSubtitles = useMemo(() => mediaMetadata?.subtitles.length !== 0, [mediaMetadata]);
    const canSelectTime = useMemo(() => {
        return mediaMetadata &&
            mediaDuration > 0 &&
            (!softCodedSubtitles || mediaMetadata.subtitles.length === 0);
    }, [mediaMetadata, softCodedSubtitles]);

    const save = () => {
        onSave({
            position,
            yOffsetRatio: yOffset,
            xOffsetRatio: xOffset,
            softCodedSubtitles,
            filterNoiseText,
            timeStart,
            timeEnd
        });
        onClose();
    }

    useEffect(() => {
        if (show && videoFile) {
            const videoProbe = new MediaProbe();
            videoProbe.load(videoFile).then(() => {
                setMediaMetadata(videoProbe.getMetaData());
            }).catch(e => {
                toast.error(`Failed to load media metadata. ${e.message}`);
                onClose()
            }).finally(() => {
                videoProbe.unload();
            })
        } else if (!videoFile) {
            setMediaMetadata(metadata || null);
        }
    }, [show, videoFile, metadata]);

    useEffect(() => {
        if (!show) {
            setTimeout(() => setSelectingCustomRegion(false), 350);
            setYOffset(undefined);
            setXOffset(undefined);
            setTimeStart(0);
            setTimeEnd(undefined);
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            setPosition(initSettings.position);
            setSoftCodedSubtitles(initSettings.softCodedSubtitles);
            setYOffset(initSettings.yOffsetRatio);
            setXOffset(initSettings.xOffsetRatio);
            setTimeStart(initSettings.timeStart || 0);
            setTimeEnd(initSettings.timeEnd);
        }
    }, [show]);

    const handleTimeRangeChange = (values: { start: number; end: number }) => {
        setTimeStart(values.start);
        setTimeEnd(values.end);
    };

    const handleRegionSelection = (xStart: number, xEnd: number, yStart: number, yEnd: number) => {
        setYOffset([yStart, yEnd]);
        setXOffset([xStart, xEnd]);
        setPosition('custom');
        setSelectingCustomRegion(false);
    }

    return (<MultiViewModal
        currentIdx={selectingCustomRegion ? 1 : 0}
        onBack={() => setSelectingCustomRegion(false)}
        show={show}
        onClose={onClose}
        maxWidth={'md'}
        title="Advanced Settings"
    >
        {mediaMetadata === null ? (
            <div className="w-full flex justify-center items-center min-h-60">
                <Spinner size={8} className="ml-2 fill-blue-300"/>
            </div>
        ) : (
            <div className="px-8 pb-6 mt-5">
                {(hasSoftCodedSubtitles && !isAudioOnly) && (
                    <div>
                        <div className="mb-0 mt-5">
                            <label htmlFor="default-input" className="block mb-1 text-sm font-black text-gray-900 dark:text-white">
                                Soft-coded Subtitles
                            </label>
                            <div className="flex flex-row items-center justify-between">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Extract embedded subtitle track from the file. Disable to extract from video frames instead.</p>
                                <div className="flex-shrink-0 ml-4 mr-3">
                                    <ToggleSwitch
                                        color="blue"
                                        theme={{
                                            toggle: {
                                                base: "toggle-bg rounded-full border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700 w-11 h-6 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5",
                                                checked: { color: { blue: "bg-blue-700 border-blue-700" } },
                                            },
                                        }}
                                        disabled={mediaDuration === 0}
                                        checked={softCodedSubtitles}
                                        onChange={setSoftCodedSubtitles}
                                    />
                                </div>
                            </div>
                        </div>
                        {!softCodedSubtitles && <hr className="my-6 dark:border-gray-700"/>}
                    </div>
                )}
                {
                    canSelectTime && (
                        <div>

                            <div className="mb-5">
                                <label htmlFor="default-input" className="block mb-1 text-sm font-black text-gray-900 dark:text-white">
                                    Time Range
                                </label>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Select the time range from which subtitles will be extracted.</p>
                            </div>

                            <TimeRangePicker
                                minValue={0}
                                maxValue={mediaDuration}
                                startValue={timeStart}
                                endValue={timeEnd}
                                onChange={handleTimeRangeChange}
                            />
                        </div>
                    )
                }

                {((!hasSoftCodedSubtitles || !softCodedSubtitles) && !isAudioOnly) && (
                    <div>
                        {canSelectTime && <hr className="my-6 dark:border-gray-700"/>}
                        <div className="mb-0 mt-5">
                            <label htmlFor="default-input" className="block mb-1 text-sm font-black text-gray-900 dark:text-white">
                                Remove Non-Dialogue Text
                            </label>
                            <div className="flex flex-row items-center justify-between">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Remove non-dialogue text, like credits, garbled text, and watermarks.</p>
                                <div className="flex-shrink-0 ml-4 mr-3">
                                    <ToggleSwitch
                                        color="blue"
                                        theme={{
                                            toggle: {
                                                base: "toggle-bg rounded-full border-gray-200 bg-gray-200 dark:border-gray-600  w-11 h-6 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5",
                                                checked: { color: { blue: "bg-blue-700 border-blue-700 bg-blue-600" } },
                                            },
                                        }}
                                        disabled={mediaDuration === 0}
                                        checked={filterNoiseText}
                                        onChange={setFilterNoiseText}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {canSelectCustomRegion && <hr className="my-6 dark:border-gray-700"/>}

                {canSelectCustomRegion && (
                    <div>
                        <div className="mb-5">
                            <label htmlFor="default-input" className="block mb-1 text-sm font-black text-gray-900 dark:text-white">
                                Subtitle Region
                            </label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Choose the region of the video frame from which subtitles will be extracted.</p>
                        </div>
                        <div className="flex">
                            <SubtitlePositionButton position='auto' selected={position === 'auto'} onClick={() => setPosition('auto')}/>
                            <SubtitlePositionButton position='bottom' selected={position === 'bottom'} onClick={() => setPosition('bottom')}/>
                            <SubtitlePositionButton position='middle' selected={position === 'middle'} onClick={() => setPosition('middle')}/>
                            <SubtitlePositionButton position='top' selected={position === 'top'} onClick={() => setPosition('top')}/>
                            {videoFile && (
                                <SubtitlePositionButton position='custom' selected={position === 'custom'} onClick={() => setSelectingCustomRegion(true)}/>
                            )}
                        </div>
                    </div>
                )}
                <PrimaryButton className="w-full mt-12 mb-4 me-2" onClick={save}>
                    <span className="flex justify-center items-center">Save</span>
                </PrimaryButton>
            </div>
        )}
        <div data-title="Select Subtitle Region">
            {videoFile && selectingCustomRegion && (
                <SubtitleRegionConfig
                    onSelection={handleRegionSelection}
                    onClose={() => setSelectingCustomRegion(false)}
                    offsetRatio={xOffset && yOffset ? [xOffset[0], xOffset[1], yOffset[0], yOffset[1]] : undefined}
                    videoFile={videoFile}
                />
            )}
        </div>
    </MultiViewModal>)
}

type SubtitlePositionButtonProps = {
    position: SubtitlePosition,
    selected: boolean,
    onClick: () => void,
}

function SubtitlePositionButton({position, selected, onClick}: SubtitlePositionButtonProps) {
    let justifyContent = 'justify-center';
    let padding = '';

    if (position === 'top') {
        justifyContent = 'justify-start';
        padding = 'pt-2';
    } else if (position === 'bottom') {
        justifyContent = 'justify-end';
        padding = 'pb-2';
    }

    return (
        <div
            onClick={onClick}
            className={`cursor-pointer bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 w-1/6 flex-col text-center mr-2`}
        >
            <div
                className={`transition-all bg-gray-100 dark:bg-gray-800 ${selected ? 'border-2 border-blue-600' : 'border-2 border-white dark:border-gray-900 hover:border-gray-300 dark:hover:border-gray-600'} rounded w-full h-16 lg:h-14 flex flex-col items-center ${justifyContent} ${padding}`}
            >
                {position === 'auto' ? (
                    <FontAwesomeIcon icon={faMagicWandSparkles} className="text-gray-400 dark:text-gray-500 text-xl"/>
                ) : position === 'custom' ? (
                    <FontAwesomeIcon icon={faGear} className="text-gray-400 dark:text-gray-500 text-xl"/>
                ) : (
                    <>
                        <div className="h-1 bg-gray-300 dark:bg-gray-600 rounded-full w-[80%]"></div>
                        <div className="h-1 bg-gray-300 dark:bg-gray-600 rounded-full w-[60%] mt-1"></div>
                    </>
                )}
            </div>
            <span className="text-sm mt-2">{ucFirst(position)}</span>
        </div>
    )
}
